import { ApolloError } from 'apollo-client'
import * as React from 'react'
import { useMutation } from 'react-apollo'
import uuid from 'uuid/v4'

import { getParentLoginResetUrl } from 'main/components/common/AuthModal/ssoNavigation'
import Loader from 'main/components/common/Loader'
import { CredentialsFields } from 'main/gql/credentials'
import { CREATE_PARENT_USER_MUTATION, CreateParentUser } from 'main/gql/user'
import useSSOContext from 'main/hooks/useSSOContext'
import segment from 'util/analytics/segment'

import MutationError, { ErrorType } from './Error'
import { ParentContainer, ParentSmallContainer } from './InitialParentSignUpForm.styles'

interface IProps {
  onComplete: (credentials: CredentialsFields) => void
}

export default function InitialParentSignUpFormOnboarding(props: IProps) {
  const { onComplete } = props
  const { ssoContext } = useSSOContext()
  const [isConsumed, setConsumed] = React.useState<boolean>(false)
  const [error, setError] = React.useState<ErrorType | undefined>()
  const [createParentUser, { data, loading: isLoading, error: apolloError }] = useMutation(
    CREATE_PARENT_USER_MUTATION,
  )

  React.useEffect(() => {
    if (!ssoContext) {
      setError(ErrorType.Generic)
    }
  })

  React.useEffect(() => {
    async function doAsync() {
      if (!error && !!ssoContext && !data && !isLoading && !isConsumed) {
        setConsumed(true)
        try {
          const { source, signupData } = ssoContext
          await createParentUser({
            variables: {
              user: {
                firstName: signupData?.firstName || '',
                email: signupData?.email || '',
                family: {},
                parentTosAgreed: true,
                password: uuid(),
                signUpSource: !!source ? source.toString() : '',
              },
            },
          })
        } catch (err) {
          setError(ErrorType.Generic)
        }
      }
    }
    doAsync()
  }, [data])

  React.useEffect(() => {
    if (!error && isConsumed && !!data) {
      handleCompleted(data)
    }
  }, [data])

  React.useEffect(() => {
    if (!error && !!apolloError) {
      handleError(apolloError)
    }
  }, [error, apolloError])

  return (
    <ParentContainer>
      {error ? (
        <ParentSmallContainer>
          <MutationError type={error} onRequestLogin={onRequestLogin} />
        </ParentSmallContainer>
      ) : (
        <Loader />
      )}
    </ParentContainer>
  )

  function handleError(apolloErr: ApolloError) {
    const messages = apolloErr.graphQLErrors.map(e => e.message)
    if (messages.includes('email has already been taken')) {
      setError(ErrorType.EmailExists)
    } else {
      setError(ErrorType.Generic)
    }
  }

  function onRequestLogin() {
    const parentJoinUrl = getParentLoginResetUrl() // something seriously wrong, try to limit confounding factors by dropping context
    window.location.replace(parentJoinUrl)
  }

  function handleCompleted(resultData: CreateParentUser) {
    segment.track('New Parent Lead', ssoContext?.segmentData?.data || {})

    const credentials = resultData?.createParentUser?.credentials
    if (!credentials) {
      setError(ErrorType.Generic)
      return
    }

    onComplete(credentials)
  }
}
